import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Badge,
  BlockStack,
  InlineStack,
  Text,
  SkeletonBodyText,
  Box,
  Divider,
  DataTable,
  Button,
  TextField,
  Tabs,
} from '@shopify/polaris';
import { patientsApi } from '@/services';
import type { Rental, RentalStatus, PatientClinicAssignment } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: '',
  });

  const { data: patient, isLoading: patientLoading } = useQuery({
    queryKey: ['patient', id],
    queryFn: () => patientsApi.getById(id!),
    enabled: !!id,
  });

  const { data: rentals, isLoading: rentalsLoading } = useQuery({
    queryKey: ['patient-rentals', id],
    queryFn: () => patientsApi.getRentals(id!, { limit: 10 }),
    enabled: !!id && selectedTab === 1,
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof editForm) => patientsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', id] });
      setIsEditing(false);
    },
  });

  const removeFromClinicMutation = useMutation({
    mutationFn: ({ clinicId }: { clinicId: string }) =>
      patientsApi.removeFromClinic(id!, clinicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', id] });
    },
  });

  const getStatusBadge = (status: RentalStatus) => {
    const statusConfig: Record<string, { tone: 'success' | 'warning' | 'critical' | 'info' | undefined }> = {
      pending: { tone: undefined },
      processing: { tone: 'info' },
      shipped: { tone: 'info' },
      in_use: { tone: 'success' },
      returned: { tone: 'warning' },
      complete: { tone: 'success' },
      cancelled: { tone: 'critical' },
    };
    const config = statusConfig[status] || { tone: undefined };
    return <Badge tone={config.tone}>{status.replace('_', ' ')}</Badge>;
  };

  if (patientLoading) {
    return (
      <Page title="Patient Details">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (!patient) {
    return (
      <Page
        title="Patient Not Found"
        backAction={{ content: 'Back', onAction: () => navigate('/patients') }}
      >
        <Card>
          <Text as="p">The requested patient could not be found.</Text>
        </Card>
      </Page>
    );
  }

  const rentalRows = (rentals?.data || []).map((rental: Rental) => [
    <Button key={rental.id} variant="plain" onClick={() => navigate(`/rentals/${rental.id}`)}>
      {orderNumber(rental)}
    </Button>,
    rental.rentalStartDate ? new Date(rental.rentalStartDate).toLocaleDateString() : 'N/A',
    rental.rentalEndDate ? new Date(rental.rentalEndDate).toLocaleDateString() : 'N/A',
    `$${Number(rental.totalAmount).toFixed(2)}`,
    getStatusBadge(rental.rentalStatus),
  ]);

  const tabs = [
    { id: 'details', content: 'Details', accessibilityLabel: 'Patient details' },
    { id: 'rentals', content: 'Rentals', accessibilityLabel: 'Patient rentals' },
    { id: 'clinics', content: 'Clinics', accessibilityLabel: 'Patient clinics' },
  ];

  return (
    <Page
      title={`${patient.firstName} ${patient.lastName}`}
      backAction={{ content: 'Back', onAction: () => navigate('/patients') }}
      titleMetadata={
        <Badge tone={patient.status === 'active' ? 'success' : undefined}>
          {patient.status}
        </Badge>
      }
      secondaryActions={[
        {
          content: isEditing ? 'Cancel' : 'Edit',
          onAction: () => {
            if (isEditing) {
              setIsEditing(false);
            } else {
              setEditForm({
                firstName: patient.firstName,
                lastName: patient.lastName,
                email: patient.email || '',
                phone: patient.phone || '',
                streetAddress: patient.streetAddress || '',
                city: patient.city || '',
                province: patient.province || '',
                postalCode: patient.postalCode || '',
              });
              setIsEditing(true);
            }
          },
        },
      ]}
    >
      <Layout>
        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Total Rentals</Text>
                <Text as="p" variant="headingXl">
                  {patient._count?.rentals ?? 0}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="p" tone="subdued">Clinics</Text>
                <Text as="p" variant="headingXl">
                  {patient.clinicAssignments?.length ?? 0}
                </Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Main Content */}
        <Layout.Section>
          <Card padding="0">
            <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />

            {/* Details Tab */}
            {selectedTab === 0 && (
              <Box padding="400">
                {isEditing ? (
                  <BlockStack gap="400">
                    <TextField
                      label="First Name"
                      value={editForm.firstName}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, firstName: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Last Name"
                      value={editForm.lastName}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, lastName: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={editForm.email}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, email: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Phone"
                      type="tel"
                      value={editForm.phone}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, phone: value }))}
                      autoComplete="off"
                    />
                    <TextField
                      label="Street Address"
                      value={editForm.streetAddress}
                      onChange={(value) => setEditForm((prev) => ({ ...prev, streetAddress: value }))}
                      autoComplete="off"
                    />
                    <InlineStack gap="400">
                      <Box width="40%">
                        <TextField
                          label="City"
                          value={editForm.city}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, city: value }))}
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Province"
                          value={editForm.province}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, province: value }))}
                          placeholder="ON"
                          autoComplete="off"
                        />
                      </Box>
                      <Box width="25%">
                        <TextField
                          label="Postal Code"
                          value={editForm.postalCode}
                          onChange={(value) => setEditForm((prev) => ({ ...prev, postalCode: value }))}
                          autoComplete="off"
                        />
                      </Box>
                    </InlineStack>
                    <InlineStack gap="200">
                      <Button
                        variant="primary"
                        onClick={() => updateMutation.mutate(editForm)}
                        loading={updateMutation.isPending}
                      >
                        Save Changes
                      </Button>
                      <Button onClick={() => setIsEditing(false)}>Cancel</Button>
                    </InlineStack>
                  </BlockStack>
                ) : (
                  <BlockStack gap="400">
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Email</Text>
                        <Text as="span">{patient.email || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Phone</Text>
                        <Text as="span">{patient.phone || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Date of Birth</Text>
                        <Text as="span">
                          {patient.dateOfBirth
                            ? new Date(patient.dateOfBirth).toLocaleDateString()
                            : 'N/A'}
                        </Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Gender</Text>
                        <Text as="span">{patient.gender || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Age</Text>
                        <Text as="span">{patient.age != null ? String(patient.age) : 'N/A'}</Text>
                      </InlineStack>
                    </BlockStack>

                    <Divider />

                    <BlockStack gap="200">
                      <Text as="p" fontWeight="semibold">Address</Text>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Street</Text>
                        <Text as="span">{patient.streetAddress || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">City / Province</Text>
                        <Text as="span">
                          {[patient.city, patient.province].filter(Boolean).join(', ') || 'N/A'}
                        </Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Postal Code</Text>
                        <Text as="span">{patient.postalCode || 'N/A'}</Text>
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Country</Text>
                        <Text as="span">{patient.country || 'N/A'}</Text>
                      </InlineStack>
                    </BlockStack>

                    <Divider />

                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Doctor</Text>
                        {patient.referredByDoctor ? (
                          <Button
                            variant="plain"
                            onClick={() => navigate(`/doctors/${patient.referredByDoctor!.id}`)}
                          >
                            Dr. {patient.referredByDoctor.firstName}{' '}
                            {patient.referredByDoctor.lastName}
                          </Button>
                        ) : (
                          <Text as="span">N/A</Text>
                        )}
                      </InlineStack>
                      <InlineStack align="space-between">
                        <Text as="span" tone="subdued">Created</Text>
                        <Text as="span">{new Date(patient.createdAt).toLocaleDateString()}</Text>
                      </InlineStack>
                    </BlockStack>
                  </BlockStack>
                )}
              </Box>
            )}

            {/* Rentals Tab */}
            {selectedTab === 1 && (
              <>
                {rentalsLoading ? (
                  <Box padding="400">
                    <SkeletonBodyText lines={5} />
                  </Box>
                ) : rentalRows.length === 0 ? (
                  <Box padding="800">
                    <Text as="p" tone="subdued" alignment="center">
                      No rentals found for this patient
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Order', 'Start Date', 'End Date', 'Total', 'Status']}
                    rows={rentalRows}
                  />
                )}
              </>
            )}

            {/* Clinics Tab */}
            {selectedTab === 2 && (
              <>
                {!patient.clinicAssignments || patient.clinicAssignments.length === 0 ? (
                  <Box padding="800">
                    <Text as="p" tone="subdued" alignment="center">
                      This patient is not assigned to any clinic
                    </Text>
                  </Box>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text']}
                    headings={['Clinic', 'Assigned Date', 'Status', 'Action']}
                    rows={patient.clinicAssignments.map((assignment: PatientClinicAssignment) => [
                      assignment.clinic ? (
                        <Button
                          key={assignment.clinicId}
                          variant="plain"
                          onClick={() => navigate(`/clinics/${assignment.clinicId}`)}
                        >
                          {assignment.clinic.name}
                        </Button>
                      ) : (
                        assignment.clinicId
                      ),
                      new Date(assignment.assignedAt).toLocaleDateString(),
                      <Badge
                        key={`status-${assignment.id}`}
                        tone={assignment.status === 'active' ? 'success' : undefined}
                      >
                        {assignment.status}
                      </Badge>,
                      <Button
                        key={`remove-${assignment.id}`}
                        tone="critical"
                        variant="plain"
                        loading={removeFromClinicMutation.isPending}
                        onClick={() =>
                          removeFromClinicMutation.mutate({ clinicId: assignment.clinicId })
                        }
                      >
                        Remove
                      </Button>,
                    ])}
                  />
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
